import { Box, Link } from '@mui/material';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CustomTable from '@shared/components/others/CustomTable/CustomTable';
import OverflowTooltip from '@shared/components/ui/OverflowTooltip';
import { dateFormat, dateFormatWithoutHours, isMac, REQUEST_STATUS } from '@shared/constants/otherConstants';
import {
    UI_AUTO_TEST_RESULTS_PATH,
    UI_FUNCTIONAL_TESTING_PATH,
    UI_HAND_TEST_RESULTS_PATH,
    UI_SCHEMES_PATH,
    UI_STANDS_PATH,
} from '@shared/constants/pathsConstants';
import {
    selectGetTestResultsBranchesFilter,
    selectGetTestResultsBranchesFilterStatus,
    selectGetTestResultsBuildsFilter,
    selectGetTestResultsBuildsFilterStatus,
    selectGetTestResultsStatus,
    selectTestResults,
    selectTestResultsCount,
    selectTestRun,
} from '@pages/testruns/store/selectors';
import { formatDuration, getObjectLinkById, toTitleCase } from '@shared/utils/utilsOld';
import TestrunStatusChip from './TestrunStatusChip';
import TestrunResultsTableToolbar from './TestrunResultsTableToolbar';
import { AUTO_TEST_RESULT, HAND_TEST_RUN_STATUS } from '@pages/testruns/constants/testrunsConstants';
import TestResultChip from './TestResultChip';
import { useListState } from '@shared/hooks/useListState';
import {
    getTestResultsBranchesFilterRequest,
    getTestResultsBuildsFilterRequest,
    getTestResultsRequest,
} from '@pages/testruns/store';
import { useCreatedByUserColumn } from '@shared/hooks/useCreatedByUserColumn';
import UtmsGlobalFilterTextField from '@shared/components/ui/UtmsGlobalFilterTextField';
import ToggleMoreFiltersButton from '@shared/components/others/ToggleMoreFiltersButton/ToggleMoreFiltersButton';
import { getDefectLinkById, getSelectAllCheckboxState } from '@shared/utils/utils';
import ComponentWithChunks from '@shared/components/others/ComponentWithChunk/ComponentWithChunk';
import { useTestrunsChunk } from '../hooks/useTestrunsChunk';
import UtmsPagination from '@shared/components/ui/UtmsPagination';
import DefectsPopover from '@pages/defects/components/DefectsPopover';
import TestingEndDateCell from '@pages/testplans/components/TestingEndDateCell';
import { selectGetTeamsStatus, selectTeams } from '@features/teams/store/selectors';
import UtmsChip from '@shared/components/ui/UtmsChip';
import { getTeamsRequest } from '@features/teams/store';
import JiraBugsCell from './JiraBugsCell';

const TestrunResultsTable = (props) => (
    <ComponentWithChunks
        hooksArray={[useTestrunsChunk]}
        featuresArray={['testRuns']}
        Component={TestrunResultsTableComponent}
        {...props}
    />
);

const TestrunResultsTableComponent = ({ data, defectId, customColumns }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const testrun = useSelector(selectTestRun);
    const getTestResultsStatus = useSelector(selectGetTestResultsStatus);
    const testRunResults = useSelector(selectTestResults);
    const shortMode = Boolean(defectId);

    const {
        rowSelection,
        setRowSelection,
        pagination,
        setPagination,
        globalFilter,
        setGlobalFilter,
        showColumnFilters,
        setShowColumnFilters,
        sorting,
        setSorting,
        columnFilters,
        setColumnFilters,
    } = useListState('testrunResultsTable', false, getTestResultsRequest, {
        testRunId: testrun?.pk,
        latestOnly: shortMode ? null : true,
        defectId,
        disableUrlParams: shortMode,
    });
    const testResultsCount = useSelector(selectTestResultsCount);
    const buildsFilter = useSelector(selectGetTestResultsBuildsFilter);
    const branchesFilter = useSelector(selectGetTestResultsBranchesFilter);
    const buildsStatus = useSelector(selectGetTestResultsBuildsFilterStatus);
    const branchesStatus = useSelector(selectGetTestResultsBranchesFilterStatus);
    const getTeamsStatus = useSelector(selectGetTeamsStatus);
    const teams = useSelector(selectTeams);
    const selectedTestRunResultsIds = useMemo(() => {
        return Object.entries(rowSelection)
            .filter((entry) => entry[1])
            .map((entry) => entry[0]);
    }, [rowSelection]);
    const [prevSelectedStaticRowIndex, setPrevSelectedStaticRowIndex] = useState(null);
    const createdByUserColumn = useCreatedByUserColumn({
        columnFilters,
        setColumnFilters,
        accessorKey: 'assigned_user',
        header: 'assigned_user',
    });
    const [defectsPopoverAnchorEl, setDefectsPopoverAnchorEl] = useState(null);
    const [defectsPopoverByTestresult, setDefectsPopoverByTestresult] = useState(null);
    const isLoadingTestresults = getTestResultsStatus === REQUEST_STATUS.LOADING;
    const testresultsWithPrefixes = (data || testRunResults).map((testresult) => ({
        ...testresult,
        pk: `${testresult.testcase.is_handtest ? 'HTR' : 'ATR'}-${testresult.pk}`,
    }));

    const handleOpenDefectsPopover = (e, defects) => {
        e.stopPropagation();
        setDefectsPopoverAnchorEl(e.currentTarget);
        setDefectsPopoverByTestresult(defects);
    };

    const handleClearRowSelection = () => {
        setRowSelection({});
    };

    const columns = useMemo(
        () => [
            {
                accessorKey: 'pk',
                header: 'ID',
                size: 50,
                Cell: ({ cell }) => <Box sx={{ whiteSpace: 'nowrap' }}>{`${cell.getValue()}`}</Box>,
            },
            {
                accessorKey: 'testcaserun',
                accessorFn: (originalRow) => originalRow?.testcase?.name || '',
                header: t('test_case_run'),
                size: 200,
                Cell: ({ cell }) => {
                    const result = cell.row.original;
                    const { pk, testcase } = result;

                    const navigatePath = `/${UI_FUNCTIONAL_TESTING_PATH}/${
                        testcase.is_handtest ? UI_HAND_TEST_RESULTS_PATH : UI_AUTO_TEST_RESULTS_PATH
                    }/${pk.split('-')[1]}`;

                    if (!testcase) {
                        return;
                    }

                    return (
                        <OverflowTooltip title={testcase.name}>
                            <Link
                                underline="hover"
                                onClick={(e) => {
                                    e.preventDefault();
                                    navigate(navigatePath);
                                }}
                                href={navigatePath}
                                sx={{ cursor: 'pointer' }}
                            >
                                {testcase.name}
                            </Link>
                        </OverflowTooltip>
                    );
                },
            },
            createdByUserColumn,
            {
                accessorKey: 'start_date',
                header: toTitleCase(t('start_date')),
                enableSorting: false,
                enableColumnFilter: false,
                Cell: () => {
                    const date = testrun?.testplan?.start_date;
                    return date ? dayjs(date).format(dateFormatWithoutHours) : t('no_info');
                },
            },
            {
                accessorKey: 'branch',
                header: t('branch'),
                enableSorting: false,
                size: 150,
                filterVariant: 'autocomplete',
                filterSelectOptions: branchesFilter?.items?.map((item) => ({
                    label: item,
                    value: item,
                })),

                muiFilterAutocompleteProps: {
                    onOpen: () => dispatch(getTestResultsBranchesFilterRequest({ testrun: testrun.pk })),
                    loading: branchesStatus === REQUEST_STATUS.LOADING,
                    forcePopupIcon: true,
                },

                Cell: ({ cell }) => {
                    const branch = cell.row.original.branch;
                    if (!branch) return t('not_marked');
                    return <OverflowTooltip title={branch}>{branch}</OverflowTooltip>;
                },
            },
            {
                accessorKey: 'build',
                header: t('build'),
                enableSorting: false,
                size: 150,
                filterVariant: 'autocomplete',
                filterSelectOptions: buildsFilter?.items?.map((item) => ({
                    label: item,
                    value: item,
                })),
                muiFilterAutocompleteProps: {
                    onOpen: () => dispatch(getTestResultsBuildsFilterRequest({ testrun: testrun.pk })),
                    loading: buildsStatus === REQUEST_STATUS.LOADING,
                    forcePopupIcon: true,
                },
                Cell: ({ cell }) => {
                    const build = cell.row.original.build;
                    if (!build) return t('not_marked');
                    return <OverflowTooltip title={build}>{build}</OverflowTooltip>;
                },
            },
            {
                accessorKey: 'defects',
                header: t('defects'),
                size: 110,
                Cell: ({ cell }) => {
                    const testresult = cell.row.original;
                    const defects = cell.getValue();
                    const defect = defects[0];

                    if (!defects || !defects.length) {
                        return '–';
                    }

                    return (
                        <>
                            {defects.length > 1 ? (
                                <Link
                                    underline="hover"
                                    sx={{ cursor: 'pointer' }}
                                    onClick={(e) => handleOpenDefectsPopover(e, testresult.defects)}
                                >
                                    {t('defectsWithCount', { count: defects.length })}
                                </Link>
                            ) : (
                                <OverflowTooltip title={defect.name}>
                                    <Link
                                        underline="hover"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            navigate(getDefectLinkById(defect.pk));
                                        }}
                                        sx={{ cursor: 'pointer' }}
                                        href={getDefectLinkById(defect.pk)}
                                    >
                                        {defect.name}
                                    </Link>
                                </OverflowTooltip>
                            )}
                        </>
                    );
                },
                enableColumnFilter: false,
                enableSorting: false,
            },
            {
                accessorKey: 'result',
                header: t('result'),
                enableSorting: false,
                filterVariant: 'select',
                filterSelectOptions: Object.keys(AUTO_TEST_RESULT).map((item) => ({
                    label: <TestResultChip result={item} />,
                    value: item.toLowerCase(),
                })),
                size: 160,
                Cell: ({ cell }) => {
                    const result = cell.getValue();
                    if (!result) {
                        return '–';
                    }

                    return <TestResultChip result={result} />;
                },
            },
            {
                accessorKey: 'status',
                header: t('status'),
                enableSorting: false,
                size: 175,
                Cell: ({ cell }) => <TestrunStatusChip status={cell.getValue()} />,
                filterVariant: 'select',
                filterSelectOptions: Object.entries(HAND_TEST_RUN_STATUS).map(([key, label]) => ({
                    label: <TestrunStatusChip status={label} />,
                    value: key.toLowerCase(),
                })),
            },
            {
                accessorKey: 'team',
                header: t('team'),
                size: 130,
                Cell: ({ cell }) => {
                    const team = cell.getValue();
                    return team && <UtmsChip label={team} />;
                },
                muiFilterAutocompleteProps: {
                    onOpen: () => dispatch(getTeamsRequest()),
                    loading: getTeamsStatus === REQUEST_STATUS.LOADING,
                    forcePopupIcon: true,
                },
                filterVariant: 'autocomplete',
                filterSelectOptions: teams.map((team) => ({
                    label: team,
                    value: team,
                })),
            },
            {
                accessorKey: 'end_date',
                header: toTitleCase(t('due_date')),
                enableColumnFilter: false,
                enableSorting: false,
                Cell: () => <TestingEndDateCell date={testrun?.testplan?.end_date} />,
            },
            {
                accessorKey: 'type',
                accessorFn: (originalRow) =>
                    originalRow.testcase && originalRow.testcase.is_handtest ? 'true' : 'false',
                header: t('type'),
                size: 125,
                filterVariant: 'select',
                filterSelectOptions: [
                    { label: t('hand-test'), value: 'handtest' },
                    { label: t('auto-test'), value: 'autotest' },
                ],
                enableSorting: false,
                Cell: ({ cell }) => (cell.getValue() === 'true' ? t('hand-test') : t('auto-test')),
            },
            {
                accessorKey: 'duration',
                header: t('duration'),
                size: 140,
                enableColumnFilter: false,
                Cell: ({ cell }) => formatDuration(cell.getValue()),
            },
            {
                accessorKey: 'jira_bugs',
                header: t('jira_bugs'),
                size: 125,
                enableColumnFilter: false,
                enableSorting: false,
                Cell: ({ cell }) => <JiraBugsCell jiraBugs={cell.getValue()} testRunResultId={cell.row.original.pk} />,
            },
            {
                accessorKey: 'stand',
                header: t('stand'),
                enableSorting: false,
                size: 125,
                Cell: ({ cell }) => {
                    const standId = cell.getValue();

                    if (!standId) {
                        return '–';
                    }

                    return (
                        <>
                            <Link
                                underline="hover"
                                sx={{ cursor: 'pointer' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(getObjectLinkById('pk', standId, false, UI_STANDS_PATH));
                                }}
                                href={getObjectLinkById('pk', standId, false, UI_STANDS_PATH)}
                            >
                                {standId}
                            </Link>
                        </>
                    );
                },
            },
            {
                accessorKey: 'scheme',
                accessorFn: (originalRow) => originalRow.scheme?.name,
                header: t('scheme'),
                enableSorting: false,
                size: 125,
                Cell: ({ cell }) => {
                    const scheme = cell.row.original.scheme;

                    if (!scheme) {
                        return '–';
                    }

                    return (
                        <Link
                            underline="hover"
                            sx={{ cursor: 'pointer' }}
                            onClick={(e) => {
                                e.preventDefault();
                                navigate(`/${UI_SCHEMES_PATH}/${scheme.pk}`);
                            }}
                            href={`/${UI_SCHEMES_PATH}/${scheme.pk}`}
                        >
                            {scheme.name}
                        </Link>
                    );
                },
            },
            {
                accessorKey: 'start_time',
                header: t('started'),
                size: 150,
                Cell: ({ cell }) => {
                    const date = cell.getValue();
                    return date ? dayjs(date).format(dateFormat) : t('no_info');
                },
                filterVariant: 'date',
                muiFilterDatePickerProps: ({ column }) => ({
                    value: column.getFilterValue() ? dayjs(column.getFilterValue()) : null,
                    onChange: (newValue) => {
                        column.setFilterValue(newValue ? newValue.toISOString() : null);
                    },
                }),
            },
        ],
        [
            navigate,
            t,
            branchesFilter?.items,
            buildsFilter?.items,
            branchesStatus,
            dispatch,
            buildsStatus,
            getTeamsStatus,
            teams,
            createdByUserColumn,
            testrun?.pk,
            testrun?.testplan?.start_date,
            testrun?.testplan?.end_date,
        ],
    );

    return (
        <>
            <CustomTable
                tableId="testrun-results-table"
                columns={
                    customColumns ? columns.filter((column) => customColumns.includes(column.accessorKey)) : columns
                }
                data={testresultsWithPrefixes}
                variant={shortMode ? 'elevation' : 'outlined'}
                toolbar={
                    shortMode
                        ? undefined
                        : (table) => (
                              <Box
                                  sx={{
                                      display: 'flex',
                                      gap: 2,
                                      alignItems: 'center',
                                      mb: 2,
                                      mt: '1px',
                                      whiteSpace: 'nowrap',
                                      flexWrap: 'wrap',
                                  }}
                              >
                                  <UtmsGlobalFilterTextField table={table} />
                                  <ToggleMoreFiltersButton table={table} />
                                  <TestrunResultsTableToolbar
                                      clearSelection={handleClearRowSelection}
                                      selectedTestRunResultsIds={selectedTestRunResultsIds}
                                      pagination={pagination}
                                  />
                              </Box>
                          )
                }
                state={{
                    density: 'compact',
                    showSkeletons: isLoadingTestresults,
                    rowSelection,
                    setRowSelection,
                    pagination,
                    columnFilters,
                    showColumnFilters,
                    globalFilter,
                    sorting,
                }}
                rowCount={testResultsCount}
                onPaginationChange={setPagination}
                onGlobalFilterChange={setGlobalFilter}
                onColumnFiltersChange={setColumnFilters}
                onSortingChange={setSorting}
                onShowColumnFiltersChange={setShowColumnFilters}
                enablePagination
                enableRowSelection={!shortMode}
                onRowSelectionChange={setRowSelection}
                muiSelectAllCheckboxProps={({ table }) => {
                    return getSelectAllCheckboxState(table, rowSelection);
                }}
                muiTableBodyRowProps={
                    !shortMode
                        ? ({ row, staticRowIndex, table }) => ({
                              //implement row selection click events manually
                              onClick: (e) => {
                                  const withCtrlKey = isMac ? e.metaKey : e.ctrlKey;
                                  const withShiftKey = e.shiftKey;
                                  let newRowSelection = {};

                                  if (withCtrlKey && !withShiftKey) {
                                      newRowSelection = { ...rowSelection, [row.id]: !rowSelection[row.id] };
                                  } else if (!withCtrlKey && !withShiftKey) {
                                      const clickedRowNotSingleAndSelected =
                                          Object.keys(rowSelection).length > 1 && row.id in rowSelection;

                                      if (clickedRowNotSingleAndSelected) {
                                          newRowSelection = {
                                              [row.id]: rowSelection[row.id],
                                          };
                                      } else {
                                          if (rowSelection[row.id]) {
                                              const { [row.id]: _, ...rest } = newRowSelection;
                                              newRowSelection = { ...rest };
                                          } else {
                                              newRowSelection = { [row.id]: true };
                                          }
                                      }
                                  } else if (withShiftKey) {
                                      if (prevSelectedStaticRowIndex === null) {
                                          newRowSelection = {
                                              [row.id]: !rowSelection[row.id],
                                          };
                                      } else {
                                          const start = Math.min(prevSelectedStaticRowIndex, staticRowIndex);
                                          const end = Math.max(prevSelectedStaticRowIndex, staticRowIndex);
                                          const rows = table.getRowModel().rows;
                                          const res = {};

                                          for (let i = end; i >= start; i--) {
                                              res[rows[i]['id']] = true;
                                          }

                                          newRowSelection = {
                                              ...rowSelection,
                                              ...res,
                                          };
                                      }
                                  }

                                  setPrevSelectedStaticRowIndex(
                                      Object.values(newRowSelection).filter(Boolean).length ? staticRowIndex : null,
                                  );
                                  setRowSelection(newRowSelection);
                              },
                              onDoubleClick: (e) => {
                                  e.preventDefault();
                                  const { pk, testcase } = row.original;
                                  navigate(
                                      `/${UI_FUNCTIONAL_TESTING_PATH}/${
                                          testcase.is_handtest ? UI_HAND_TEST_RESULTS_PATH : UI_AUTO_TEST_RESULTS_PATH
                                      }/${pk.split('-')[1]}`,
                                  );
                              },
                              onMouseDown: (e) => {
                                  if (e.shiftKey) {
                                      e.preventDefault();
                                  }
                              },
                              sx: {
                                  cursor: 'pointer',
                                  backgroundColor: 'inherit',
                              },
                              selected: rowSelection[row.id],
                          })
                        : undefined
                }
                renderBottomToolbar={() => (
                    <UtmsPagination
                        view="table"
                        pagination={pagination}
                        count={testResultsCount}
                        handleChangePagination={setPagination}
                        sx={{
                            '& .MuiSelect-root': {
                                marginRight: 0,
                            },
                        }}
                    />
                )}
                displayColumnDefOptions={{
                    'mrt-row-select': {
                        size: 28,
                        minSize: 28,
                        maxSize: 28,
                        muiTableHeadCellProps: {
                            sx: {
                                p: 0,
                                pt: '2px',
                            },
                        },
                        muiTableBodyCellProps: {
                            sx: {
                                p: 0,
                                pt: '4px',
                                ...(isLoadingTestresults ? { pl: '6px', pt: 1 } : undefined),
                                display: 'flex',
                                alignItems: 'flex-start',
                            },
                        },
                    },
                    muiTableBodyCellProps: {
                        sx: {
                            p: 0,
                        },
                    },
                }}
                {...(shortMode
                    ? {
                          muiTableContainerProps: {
                              sx: {
                                  maxHeight: 207,
                                  maxWidth: '100%',
                              },
                          },
                          muiTablePaperProps: {
                              sx: {
                                  minWidth: '100%',
                              },
                          },
                      }
                    : {})}
            />
            <DefectsPopover
                anchorEl={defectsPopoverAnchorEl}
                setAnchorEl={setDefectsPopoverAnchorEl}
                defects={defectsPopoverByTestresult}
            />
        </>
    );
};

export default TestrunResultsTable;
